<template>
  <HeroSection 
    title="Avec Nailit, visez loin pour visez juste"
    subtitle="Planifiez. Exécutez. Réussissez."
    bgImg="home.jpg"
    :isBig="true"
  />

  <!-- Valeur ajoutée -->
  <section class="py-16 px-64 bg-white flex flex-col items-center">
    <div class="mx-auto flex flex-col gap-16">
      <img
        :src="'/img/planning.png'"
        :alt="title"
        class="rounded-xl max-w-full h-auto object-contain shadow-lg"
        loading="lazy"
      />
      <div class="flex gap-3 items-start h-[215px]">
        <FeatureBlock
          title="Tous vos projets et vos rendez-vous, sur une seule ligne de temps"
          description="
        <p><strong>Contrairement</strong> aux outils verticaux (type calendrier classique), Nailit affiche votre activité sur une timeline horizontale intuitive :</p>
        <ul>
          <li>✅ Visualisez l'évolution de vos projets en un clin d'œil</li>
          <li>✅ Vos disponibilités jusqu'à <strong>1 an</strong> en avance !</li>
          <li>🔒 100% conçu pour les métiers avec des deadlines serrées (agences, artisans, freelances…)</li>
        </ul>
        "
        />

        <div class="border border-gray-300 h-full"></div>

        <FeatureBlock
          title="Vos disponibilités, en temps réel"
          description='
        <p>Nailit vous montre <strong>ENFIN</strong> :</p>
        <ul>
          <li>✅ Quand vous avez de la marge pour accepter un nouveau client (temps + ressources)</li>
          <li>❌ Quand vous êtes à saturation (avant de dire "oui" par erreur)</li>
          <li>👥 Qui est disponible (pas de double réservation de vos collaborateurs)</li>
        </ul>
        '
        />
      </div>

      <FeatureBlock
        title="L'antidote aux entreprises qui subissent leur croissance"
        description="
      <p>Vous en avez marre de :</p>
      <ul>
        <li>- Courir après les deadlines</li>
        <li>- Oublier des clients prometteurs</li>
        <li>- Devoir alterner entre 4 logiciels pour avoir un gestion complète</li>
      </ul>
      </br>
      <p>Avec Nailit et sa gestion tout en un du planning, des projets et de votre trésorerie, vous passez du mode 'improvisation' au mode 'maîtrise totale'. Enfin.</p>
      "
        img="croissance.jpg"
        :imgLeft="true"
        :imgWidth="400"
      />

      <FeatureBlock
        title="Anticipez les coups durs AVANT qu'ils n'arrivent"
        description='
      <p>Avec Nailit ne soyez plus <strong>jamais</strong> dans le rouge !</p>
      <p>Vous saurez :</p>
      <ul>
        <li>- Quand accepter un nouveau client pour combler un futur trou de trésorerie</li>
        <li>- Le montant minimum à facturer pour rester dans le vert</li>
        <li>- Les dates clés où relancer vos paiements en retard</li>
      </ul>
      '
        img="treso_red.png"
        :imgLeft="false"
        :imgWidth="500"
      />
    </div>
  </section>

  <!-- Social Proof -->
  <section class="py-16 px-64 bg-gray-50">
    <div class="mx-auto">
      <!-- Témoignage principal -->
      <div class="flex flex-col md:flex-row items-center bg-white rounded-lg shadow-lg p-8 mb-12">
        <div class="flex flex-col items-center w-full md:w-1/3">
          <img src="/img/dad.png" alt="Client" class="w-24 h-24 rounded-full mb-2 object-cover" />
          <img src="/img/dfblogo.png" alt="Entreprise" class="w-20 object-contain rounded-lg" />
          <span class="font-semibold mt-2">David Bougio</span>
        </div>
        <div class="w-full md:w-2/3 mt-6 md:mt-0 md:pl-8 text-gray-700 text-lg">
          “Grâce à cet outil, notre équipe a gagné en efficacité et en sérénité. La gestion des projets n'a jamais été aussi simple !”
        </div>
      </div>
      <!-- Logos clients -->
      <div class="overflow-x-auto whitespace-nowrap py-4 mb-6" v-if="false">
        <div class="flex gap-12 justify-center">
          <img src="/img/planning.png" alt="Client 2" class="h-16 object-contain" />
          <img src="/img/planning.png" alt="Client 3" class="h-16 object-contain" />
          <img src="/img/planning.png" alt="Client 4" class="h-16 object-contain" />
          <img src="/img/planning.png" alt="Client 5" class="h-16 object-contain" />
          <img src="/img/planning.png" alt="Client 6" class="h-16 object-contain" />
        </div>
      </div>
      <!-- Note moyenne -->
      <div class="flex items-center justify-center gap-2 text-yellow-500 text-xl font-bold" v-if="false">
        <span>⭐ 4.9/5</span>
        <span class="text-gray-700 font-normal text-base">sur 200 avis</span>
      </div>
    </div>
  </section>

  <!-- Fonctionnalités phares -->
  <section class="py-16 px-32 bg-white">
    <div class="mx-auto flex flex-col md:flex-row gap-12 items-end">
      <!-- 1 -->
      <div class="flex-1 flex flex-col items-center">
        <img src="/img/planning.png" alt="page planning" class="rounded-lg shadow-md mb-4 w-full max-w-xs" />
        <h3 class="text-xl font-bold mb-2">Planification</h3>
        <p class="text-gray-600 text-center">Ne vous laissez plus surprendre par votre planning !</p>
      </div>
      <!-- 2 -->
      <div class="flex-1 flex flex-col items-center">
        <img src="/img/projet.png" alt="page gestion de projet" class="rounded-lg shadow-md mb-4 w-full max-w-xs" />
        <h3 class="text-xl font-bold mb-2">Gestion des projets</h3>
        <p class="text-gray-600 text-center">Visualisez l'avancement de chaque projet et recevez des alertes personnalisées.</p>
      </div>
    </div>
    <div class="flex justify-center mt-8">
      <router-link to="/features" class="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded shadow-lg transition">
        Découvrir toutes les fonctionnalités
      </router-link>
    </div>
  </section>

  <!-- <FAQSection :faq="faq"/> -->
</template>

<script>
import HeroSection from '@/components/Showcase/HeroSection.vue';
import FeatureBlock from '@/components/Showcase/FeatureBlock.vue';
import FAQSection from '@/components/Showcase/FAQSection.vue';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getUserById, addNewUser } from "@/services/userService";

export default {
  name: 'ShowcaseHome',
  components: {
    HeroSection,
    FeatureBlock,
    FAQSection,
  },
  data() {
    return {
      faq: [
        {
          question: "Puis-je annuler à tout moment ?",
          answer: "Oui, vous pouvez annuler votre abonnement à tout moment depuis votre espace compte, sans frais cachés. cbdicjdjc hdcc gyi ez gyu fgzye fezgyo fgez ezx  zegyocgy"
        },
        {
          question: "Est-ce compatible mobile ?",
          answer: "L'outil est 100% responsive et fonctionne sur tous les appareils mobiles."
        },
        {
          question: "Comment fonctionne l'essai gratuit ?",
          answer: "Vous bénéficiez de 30 jours d'accès complet, sans engagement ni carte bancaire requise."
        },
        {
          question: "Mes données sont-elles sécurisées ?",
          answer: "Oui, toutes vos données sont chiffrées et stockées sur des serveurs sécurisés en France."
        }
      ]
    }
  },
  mounted() {
    const auth = getAuth();
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await getUserById(user.uid);
        } catch (e) {
          // Si le doc n'existe pas, on le crée
          if (e.message === "Utilisateur non trouvé") {
            await addNewUser({ uid: user.uid, email: user.email });
          }
        }
      }
    });
  }
};
</script>